import moment from 'moment'

import { CostCenterAssignment, FormattedAssignment } from './types'

/**
 * Build a display name for a structured cost center from its dimensions.
 * Joins all non-null dimension names (family, function, role, workplace) with ' - '.
 * Falls back to the CC name if no dimensions are present.
 */
const getStructuredCostCenterName = (costCenter: {
  code?: string | null
  name: string
  dimensions?: Array<{
    jobCatalogFamilyName?: string | null
    jobCatalogFunctionName?: string | null
    jobCatalogRoleName?: string | null
    workplaceName?: string | null
  }> | null
}): string => {
  const parts: string[] = []

  if (costCenter.dimensions && costCenter.dimensions.length > 0) {
    for (const dimension of costCenter.dimensions) {
      if (dimension.jobCatalogFamilyName) parts.push(dimension.jobCatalogFamilyName)
      if (dimension.jobCatalogFunctionName) parts.push(dimension.jobCatalogFunctionName)
      if (dimension.jobCatalogRoleName) parts.push(dimension.jobCatalogRoleName)
      if (dimension.workplaceName) parts.push(dimension.workplaceName)
    }
  }

  const dimensionName = parts.length > 0 ? parts.join(' - ') : costCenter.name
  return costCenter.code ? `${costCenter.code} - ${dimensionName}` : dimensionName
}

export const formatAssignments = (
  assignments: CostCenterAssignment[],
  hasNewStructureEnabled = false
): FormattedAssignment[] => {
  return assignments.map((assignment) => {
    const costCenter = assignment.costCenter

    let name: string
    let costCenterId: number | undefined

    if (hasNewStructureEnabled && costCenter) {
      // Structured mode: resolve name from dimensions, use the structured CC id
      name = getStructuredCostCenterName(costCenter)
      costCenterId = costCenter.id
    } else {
      // Legacy mode: use the CC name directly
      name = costCenter?.name ?? ''
      costCenterId = costCenter?.id
    }

    const formatted: FormattedAssignment = {
      name,
      id: String(assignment.id),
      percentage: assignment.percentage,
      startDate: assignment.startDate,
      endDate: assignment.endDate ?? undefined,
      costCenterId,
      isDefault: assignment.isDefault ?? false,
    }

    return formatted
  })
}

/**
 * Determines whether an assignment is in the future.
 * Future: startDate is strictly after today.
 * Current: startDate is today or in the past, and endDate is today or in the future (or null).
 * Past: endDate is strictly before today.
 *
 * Only future assignments should allow deletion in structured mode.
 */
export const isFutureAssignment = (startDate: string): boolean => {
  return moment(startDate).isAfter(moment(), 'day')
}
